import {BuildingData} from "../../models/remoteConstants.model";
import {useIntegrator} from "../../integrator.context";
import {UpdateBuildingCommand} from "../../commands/updateBuilding.command";
import Selectable from "../../elements/selectable.element";
import IconButton from "../../elements/IconButton";

// "Resource Variants": maps a tile-resource id → a variant building id. When this
// building is placed on a tile holding the resource, the variant is spawned
// instead (e.g. Mine on resource.gold → GoldMine). Stored on
// building.resourceVariants (logic — a placement rule, not an asset). The variant
// rides on this building's eligibility/price; it needs no tech unlock of its own.
function ResourceVariants(props: { building: BuildingData }) {
    const building = props.building;
    const {state, executeCommand} = useIntegrator();

    const variants = building.resourceVariants ?? {};
    const entries = Object.entries(variants);

    const resourceGroups = [{label: 'Resources', options: (state.resources ?? []).map(r => r.id)}];
    const buildingGroups = [{label: 'Buildings', options: (state.buildings ?? []).map(b => b.id)}];

    function commit(next: { [resourceId: string]: string }) {
        building.resourceVariants = next;
        executeCommand(new UpdateBuildingCommand(building));
    }

    function changeResource(oldRes: string, newRes: string) {
        if (newRes === oldRes) return;
        const next: { [k: string]: string } = {};
        // Preserve order, swapping the key in place (overwrites if newRes already mapped).
        for (const [k, v] of entries) next[k === oldRes ? newRes : k] = v;
        commit(next);
    }

    function changeBuilding(res: string, b: string) {
        commit({...variants, [res]: b});
    }

    function remove(res: string) {
        const next = {...variants};
        delete next[res];
        commit(next);
    }

    function add() {
        const used = new Set(Object.keys(variants));
        const firstRes = (state.resources ?? []).map(r => r.id).find(id => !used.has(id));
        const firstBuilding = (state.buildings ?? [])[0]?.id;
        if (!firstRes || !firstBuilding) return;   // nothing to map
        commit({...variants, [firstRes]: firstBuilding});
    }

    return (
        <div className={'layout vertical gap'} style={{gap: 6}}>
            {entries.length === 0 && <p className={'muted-text'}>None</p>}
            {entries.map(([res, b]) => (
                <div key={res} className={'layout horizontal center gap'} style={{gap: 6}}>
                    <Selectable value={res} groups={resourceGroups} style={{width: 130}}
                                onChange={(v) => changeResource(res, String(v))}/>
                    <span className={'fa-solid fa-arrow-right'} style={{opacity: 0.6}}/>
                    <Selectable value={b} groups={buildingGroups} style={{width: 130}}
                                onChange={(v) => changeBuilding(res, String(v))}/>
                    <IconButton icon={'trash'} onClick={() => remove(res)}/>
                </div>
            ))}
            <button className={'unit-attribute-button'} onClick={add}>Add variant</button>
        </div>
    );
}

export default ResourceVariants;
